import React from 'react'
import { graphql } from 'gatsby'
import styled, { createGlobalStyle } from 'styled-components'
import { Helmet } from 'react-helmet'
import dayjs from 'dayjs'

// Elements
import YouthText from 'img/youth/text.inline.svg'
import YouthText2 from 'img/youth/text-2.inline.svg'
import YouthLogo from 'img/youth/logo.inline.svg'
import MailIcon from 'img/mail.inline.svg'

// Components
import SEO from 'components/shared/SEO'
import Image from 'components/shared/Image'
import ImageBackground from 'components/shared/ImageBackground'
import Layout from 'components/layout/Layout'
import Content from 'components/shared/Content'
import ButtonInverse from 'components/elements/ButtonInverse'
import ParseContent from 'components/shared/ParseContent'

const YouthStyling = createGlobalStyle`
  h2 {
    font-weight: bold;
    font-family: ${({ theme }) => theme.font.family.secondaryBold};
    margin-bottom: 15px;
    letter-spacing: 2px;
    font-size: ${({ theme }) => theme.font.size['50']};
    text-transform: uppercase;
  }

  h3 {
    font-size: ${({ theme }) => theme.font.size['40']};
    @media (max-width: 767px) {
    font-size: ${({ theme }) => theme.font.size['30']};

    }
  }

  h6 {
    font-family: ${({ theme }) => theme.font.family.secondaryBold};
    font-size: ${({ theme }) => theme.font.size.xm};
    margin-bottom: 15px;
  }
`

const ToDoImage = styled(Image)`
  position: absolute !important;
  height: 100%;
  width: 45%;
  right: 0;
  top: 0;
  @media (max-width: 991px) {
    position: relative !important;
    width: 100%;
  }
`

const ConnectContent = styled(Content)`
  h2 {
    font-size: ${({ theme }) => theme.font.size['70']};
  }
`

const Activiteit = styled.div`
  .description {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3; /* number of lines to show */
    -webkit-box-orient: vertical;
  }

  .date {
    width: 58px;
    flex: 0 0 58px;
  }
`

const PageTemplate = ({
  data: {
    page: { path, yoast_meta: yoast, acf },
    allWordpressWpActiviteiten: { nodes: activiteitenNodes },
  },
}) => {
  const { intro, quote, after_quote, to_do, connect, our_church } = acf

  const activiteiten = activiteitenNodes.filter(
    (activiteit) => dayjs(activiteit.acf.date).toDate() >= new Date()
  )

  return (
    <Layout noChurch>
      <SEO yoast={yoast} path={path} article />
      <YouthStyling />

      <div className="color-background-black color-light py-5">
        {/* Intro */}
        <section className="container">
          <div className="row align-items-center justify-content-between">
            <div className="col-lg-5 pt-5">
              <Content content={intro.description} />
            </div>
            <div className="col-lg-6 mb-n5 z-index-2">
              <YouthText className="w-100 mb-n5" />
            </div>
          </div>
        </section>
        {/* Quote */}
        <section className="color-background-contrastPink">
          <div className="container container-extended pt-4">
            <div className="row justify-content-lg-end justify-content-center mt-5 mt-lg-0">
              <div className="col-lg-6 d-flex flex-column align-items-center justify-content-center px-0 pb-5 mx-n5 z-index-2 mt-5 mt-lg-0">
                <YouthLogo className="mb-4" />

                <Content content={quote.description} />
              </div>
              <div className="col-lg-6 d-flex flex-column justify-content-end px-0 ml-lg-n5 overflow-hidden">
                <Image
                  className="w-100 object-contain mb-n1"
                  src={quote.image}
                />
              </div>
            </div>
          </div>
        </section>
        {/* Na quote */}
        <section className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-9 d-flex flex-column align-items-center justify-content-center py-5 text-justify">
              <h2 className="mb-5">{after_quote.title}</h2>
              <Content className="mb-4" content={after_quote.description} />
              <ButtonInverse to={after_quote.link.url}>
                {after_quote.link.title}
              </ButtonInverse>
            </div>
          </div>
        </section>
        {/* To do */}
        <section
          style={{ minHeight: 800 }}
          className="color-background-contrastRed position-relative py-5 d-flex align-items-center"
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <h2 className="mb-4">{to_do.title}</h2>

                <Content className="mb-5" content={to_do.description} />

                <div className="mb-5">
                  {activiteiten.length > 0 ? (
                    <React.Fragment>
                      {activiteiten.map((activiteit) => {
                        return (
                          <Activiteit
                            className="col-lg-12 font-family-secondary d-flex align-items-center mb-4"
                            key={activiteit.id}
                          >
                            <div className="d-flex flex-column mr-4 pr-2 date">
                              <span className="font-size-50 line-height-1 color-main text-center">
                                {activiteit.acf.day}
                              </span>
                              <span className="font-size-xm text-uppercase text-center color-main">
                                {activiteit.acf.month}
                              </span>
                            </div>

                            <div className="d-flex flex-column">
                              <strong className="font-size-xm">
                                {activiteit.title}
                              </strong>
                              <ParseContent
                                className="description children-mb-0"
                                content={activiteit.acf.description}
                              />
                            </div>
                          </Activiteit>
                        )
                      })}
                    </React.Fragment>
                  ) : (
                    <div className="col-lg-12">
                      Geen activiteiten gevonden voor deze doelgroep
                    </div>
                  )}
                </div>

                <ButtonInverse to={to_do.link.url}>
                  {to_do.link.title}
                </ButtonInverse>
              </div>

              <div className="col-lg-6 position-unset mt-5 mt-lg-0">
                <ToDoImage src={to_do.image} />
              </div>
            </div>
          </div>
        </section>
        {/* Connect */}
        <section className="container">
          <div className="row py-5 justify-content-between">
            <div className="col-lg-6">
              <ConnectContent
                className="mb-5"
                content={connect.left_column.description}
              />

              <div>
                {connect.left_column.socials.map((social) => (
                  <a
                    className="d-flex align-items-center mb-4"
                    href={social.link.url}
                    target={social.link.target}
                  >
                    <img
                      style={{ width: 35, height: 35 }}
                      className="mr-4 object-contain"
                      src={
                        social.logo.url
                          ? social.logo.url
                          : social.logo.localFile.publicURL
                      }
                      alt=""
                    />
                    <span>{social.link.title}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="col-lg-5">
              <Image
                className="mb-5"
                style={{ width: 220, height: 220 }}
                src={connect.right_column.photo}
              />

              <Content content={connect.right_column.description} />

              <a
                className="d-flex align-items-center"
                href={connect.right_column.email.url}
              >
                <MailIcon className="mr-3" />

                <span>{connect.right_column.email.title}</span>
              </a>
            </div>
          </div>
        </section>
        <ImageBackground
          style={{ minHeight: 720 }}
          src={our_church.image}
          className="d-flex align-items-end pb-5"
        >
          <div className="container h-100">
            <div className="row">
              <div className="col-lg-10 offset-lg-2">
                <YouthText2 className="w-100" />
              </div>
            </div>
          </div>
        </ImageBackground>
        <Helmet>
          <script src="https://apps.elfsight.com/p/platform.js" defer></script>
        </Helmet>
        <section id="instagramFeed" className="container mt-5 mb-5 py-5">
          <div class="elfsight-app-216458b0-e03a-4b00-8dda-9e313cca8808"></div>
        </section>
      </div>
    </Layout>
  )
}

export const pageQuery = graphql`
  query($wordpress_id: Int) {
    page: wordpressPage(wordpress_id: { eq: $wordpress_id }) {
      title
      path

      acf {
        intro {
          description
        }

        quote {
          logo {
            localFile {
              publicURL
            }
          }

          description
          image {
            localFile {
              childImageSharp {
                fluid(quality: 100, maxWidth: 900) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }

        after_quote {
          title
          description
          link {
            title
            url
          }
        }

        to_do {
          title
          description
          link {
            title
            url
          }
          image {
            localFile {
              childImageSharp {
                fluid(quality: 100, maxWidth: 1000) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }

        connect {
          left_column {
            description
            socials {
              logo {
                localFile {
                  publicURL
                }
              }

              link {
                title
                url
                target
              }
            }
          }

          right_column {
            photo {
              localFile {
                childImageSharp {
                  fluid(quality: 100, maxWidth: 220) {
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }

            description
            email {
              title
              url
            }
          }
        }

        our_church {
          image {
            localFile {
              childImageSharp {
                fluid(quality: 100, maxWidth: 1920) {
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }
      }

      yoast_meta {
        name
        content
        property
      }
    }

    allWordpressWpActiviteiten(filter: { doelgroep: { eq: 5 } }) {
      nodes {
        id
        title
        doelgroep
        acf {
          date
          day: date(formatString: "D", locale: "nl_NL")
          month: date(formatString: "MMM", locale: "nl_NL")
          description
        }
      }
    }
  }
`

export default PageTemplate
